/**
 * Shell fur.
 *
 * Howard asked why the animals cannot hit the triangle counts a photoreal game animal
 * uses. The honest answer is that triangles were never the constraint — what was missing
 * is the *technique*. A real game animal does not get its fur from a texture painted on
 * a solid surface; it uses shell rendering (or hair cards): the body is drawn many times
 * over, each copy pushed slightly further along its own normals, with an alpha texture
 * that cuts away more of each successive layer. The eye reads the stack of cuts as
 * individual strands standing up off the skin.
 *
 * That is what makes fur look like fur rather than like a photograph of fur glued to a
 * balloon, and it is entirely procedural — no assets, no downloads.
 */
import * as THREE from 'three'

/** How many shells to stack. More is softer fur and more draw calls. */
const SHELLS = 9
/** How far the outermost shell sits off the skin, in metres. */
const FUR_DEPTH = 0.055

/**
 * A noise texture whose alpha decides which strands survive at a given shell height.
 * Each strand gets a random length; a shell keeps only the strands longer than itself.
 */
function createStrandTexture(density: number, seed: number): THREE.DataTexture {
  const size = 512
  const data = new Uint8Array(size * size * 4)
  let a = seed >>> 0
  const random = (): number => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  // Start fully transparent, then plant strands. Each strand is a small soft dot
  // rather than a single pixel: one-pixel strands tiled 26 times across a body land
  // well under a screen pixel, so they alias into salt-and-pepper static instead of
  // reading as hair. A soft footprint survives filtering.
  const strands = Math.floor(size * size * density)
  const RADIUS = 1.6
  const span = Math.ceil(RADIUS)
  for (let i = 0; i < strands; i++) {
    const cx = random() * size
    const cy = random() * size
    // Strand length, 0..1. Stored in alpha; shells test against their own height.
    const length = 0.25 + random() * 0.75
    // Slight per-strand shade variation, so the coat is not one flat colour.
    const shade = 214 + Math.floor(random() * 41)
    for (let oy = -span; oy <= span; oy++) {
      for (let ox = -span; ox <= span; ox++) {
        const x = (Math.floor(cx) + ox + size) % size
        const y = (Math.floor(cy) + oy + size) % size
        const falloff = 1 - Math.hypot(cx - (Math.floor(cx) + ox), cy - (Math.floor(cy) + oy)) / RADIUS
        if (falloff <= 0) continue
        const alpha = Math.floor(length * 255 * falloff)
        const idx = (y * size + x) * 4
        // Strands overlap; keep the longest one at each texel so the coat stays dense.
        if (alpha <= data[idx + 3]!) continue
        data[idx] = shade
        data[idx + 1] = shade
        data[idx + 2] = shade
        data[idx + 3] = alpha
      }
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  // Mipmaps, so fur at distance averages into a coat instead of sparkling. A DataTexture
  // has none by default, which is most of why the wolf read as television static.
  texture.generateMipmaps = true
  texture.minFilter = THREE.LinearMipmapLinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.anisotropy = 4
  texture.needsUpdate = true
  return texture
}

const strandCache = new Map<number, THREE.DataTexture>()
function strandsFor(seed: number): THREE.DataTexture {
  let texture = strandCache.get(seed)
  if (!texture) {
    texture = createStrandTexture(0.16, seed)
    strandCache.set(seed, texture)
  }
  return texture
}

/** Strand textures, prepared once per (seed, tiling) rather than per shell. */
const tiledCache = new Map<string, THREE.Texture>()
function tiled(source: THREE.DataTexture, tiling: number): THREE.Texture {
  const key = `${source.uuid}:${tiling}`
  let texture = tiledCache.get(key)
  if (!texture) {
    texture = source.clone()
    texture.needsUpdate = true
    texture.repeat.set(tiling, tiling)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    tiledCache.set(key, texture)
  }
  return texture
}

export interface FurOptions {
  color: number
  /** Tighter fur for a short-haired animal, longer for a bear. */
  depth?: number
  /** How many times the strand texture tiles. Higher is finer fur. */
  tiling?: number
  seed?: number
}

/**
 * Build the shell stack for a body. Returns meshes to add alongside the base body —
 * the base stays, and these sit on top of it.
 */
export function buildFurShells(
  geometry: THREE.BufferGeometry,
  options: FurOptions,
): THREE.Mesh[] {
  const depth = options.depth ?? FUR_DEPTH
  const tiling = options.tiling ?? 26
  const strands = strandsFor(options.seed ?? 1)
  const base = new THREE.Color(options.color)
  const shells: THREE.Mesh[] = []

  for (let i = 1; i <= SHELLS; i++) {
    const height = i / SHELLS

    // One texture for the whole stack. Every shell used to clone it and then set the
    // identical tiling, so a furry animal carried nine copies of the same strand map ---
    // ten animals in view meant ninety texture objects for the GPU to hold, on a game
    // that is mainly played on an iPad. The shells differ by alphaTest, not by texture.
    const map = tiled(strands, tiling)

    // Fur darkens toward the skin: light does not reach the roots. This gradient across
    // the stack is most of what sells depth.
    const shade = 0.55 + 0.45 * height
    const material = new THREE.MeshStandardMaterial({
      color: base.clone().multiplyScalar(shade),
      map,
      transparent: true,
      // Each shell keeps only strands longer than its own height, so the stack thins
      // out toward the tips exactly as real fur does.
      alphaTest: height * 0.86,
      roughness: 0.92,
      side: THREE.FrontSide,
      depthWrite: height < 0.4,
    })

    const shell = new THREE.Mesh(geometry, material)
    // Push this shell out along the surface normals.
    shell.userData.shellHeight = height * depth
    shell.scale.setScalar(1)
    shell.castShadow = false
    shell.receiveShadow = false
    shells.push(shell)
  }

  // Offsetting along normals is done in the shader, so the shell follows the body's
  // shape rather than just scaling up (which pulls fur away from thin limbs).
  for (const shell of shells) {
    const material = shell.material as THREE.MeshStandardMaterial
    const offset = shell.userData.shellHeight as number
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uShellOffset = { value: offset }
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uShellOffset;')
        .replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n  transformed += normalize(objectNormal) * uShellOffset;',
        )
    }
  }

  return shells
}
